/**
 * The one-tap Schedule flow: series → approved videos → account → when → preview (POST /api/publish-plans dry_run) → confirm (same body, dry_run:false).
 * Entry points: the Publish screen, "Schedule series" on a series, and an approved version's review screen (?series=&version=).
 */
import type { PublishPlanRequest } from "@contentops/contract";
import { useMemo, useState, type ReactNode } from "react";
import type { z } from "zod";
import { Link, navigate, useLocation } from "../../app/router";
import type { Account, Category, PublishPlanResult, SeriesDetail } from "../../api/types";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Badge, Button, Card, EmptyState, ErrorNote, PageHeader, QueryView, Segmented, Switch } from "../../components/ui";
import { useApi, useMutation, useQuery } from "../../data/hooks";
import { useAccounts, useCategories, usePublications, useQueues, useSeriesList, useYoutubeSettings } from "../../data/queries";
import { dateTime, languageName } from "../../lib/format";
import { fromLocalInput } from "../../lib/tz";
import { categoryName } from "../settings/AccountsScreen";
import { platformName, stateChip } from "./pubState";
import {
  AUDIT_WARNING, looksLikeBackfill, accountsByVersion, approvedCandidates, defaultAccount, defaultSelection, defaultTimes, isAuditWarning, isUsable, languagesOf, queueFor, sameCategory, type Candidate,
} from "./schedulePlan";

type Step = "series" | "versions" | "account" | "mode" | "preview";
type Mode = "queue" | "at" | "now";
const STEPS: { id: Step; label: string }[] = [
  { id: "series", label: "Series" }, { id: "versions", label: "Videos" }, { id: "account", label: "Account" }, { id: "mode", label: "When" }, { id: "preview", label: "Preview" },
];

export function ScheduleScreen() {
  const { search } = useLocation();
  const initialSeries = search.get("series");
  const initialVersion = search.get("version");
  const [seriesId, setSeriesId] = useState<string | null>(initialSeries);
  const [step, setStep] = useState<Step>(initialSeries ? "versions" : "series");
  const series = useSeriesList();
  const title = series.data?.find((s) => s.id === seriesId)?.title;
  const stepIdx = STEPS.findIndex((s) => s.id === step);
  return (
    <div className="stack-lg">
      <PageHeader back={<Link to="/publish" className="back-link"><Icon name="back" size={18} /> Publish</Link>} title="Schedule" subtitle={title ?? "Approved videos into the next free slots, in series order."} />
      <p className="step-head" aria-live="polite"><span className="eyebrow">Step {stepIdx + 1} of {STEPS.length}</span> <b>{STEPS[stepIdx]!.label}</b></p>
      {step === "series" || !seriesId ? (
        <SeriesStep onPick={(id) => { setSeriesId(id); setStep("versions"); }} />
      ) : (
        <Flow key={seriesId} seriesId={seriesId} initialVersion={seriesId === initialSeries ? initialVersion : null} step={step} setStep={setStep} />
      )}
    </div>
  );
}

function SeriesStep({ onPick }: { onPick: (id: string) => void }) {
  const q = useSeriesList();
  return (
    <QueryView q={q}>{(list) => {
      const ready = list.filter((s) => s.episodes_approved > 0);
      return ready.length ? (
        <ul className="cards" aria-label="Series with approved videos">
          {ready.map((s) => (
            <li key={s.id}>
              <button type="button" className="pick-card" onClick={() => onPick(s.id)}>
                <span className="list-main"><span className="list-title">{s.title}</span><span className="list-sub">{s.category} · {s.episodes_approved} approved</span></span>
                <Icon name="chevronRight" className="list-chevron" />
              </button>
            </li>
          ))}
        </ul>
      ) : <EmptyState icon="check" title="No approved videos yet">Approve a version (or import from the library) and it shows up here.</EmptyState>;
    }}</QueryView>
  );
}

function Flow({ seriesId, initialVersion, step, setStep }: { seriesId: string; initialVersion: string | null; step: Step; setStep: (s: Step) => void }) {
  const api = useApi();
  const detail = useQuery(`series/${seriesId}`, () => api.getSeries(seriesId), { staleMs: 10_000 });
  const pubs = useQuery(`publications/series_id=${seriesId}`, () => api.listPublications({ series_id: seriesId }), { staleMs: 10_000 });
  const accounts = useAccounts();
  const cats = useCategories();
  const q = { data: detail.data && pubs.data && accounts.data ? { d: detail.data, pubs: pubs.data, accounts: accounts.data } : undefined, error: detail.error ?? pubs.error ?? accounts.error, isLoading: false, refetch: () => Promise.all([detail.refetch(), pubs.refetch(), accounts.refetch()]) };
  return <QueryView q={q}>{(x) => <FlowBody {...x} cats={cats.data ?? []} initialVersion={initialVersion} step={step} setStep={setStep} />}</QueryView>;
}

function FlowBody({ d, pubs, accounts, cats, initialVersion, step, setStep }: { d: SeriesDetail; pubs: Parameters<typeof accountsByVersion>[0]; accounts: Account[]; cats: Category[]; initialVersion: string | null; step: Step; setStep: (s: Step) => void }) {
  const api = useApi();
  const toast = useToast();
  const queues = useQueues();
  const yt = useYoutubeSettings();
  const cands = useMemo(() => approvedCandidates(d), [d]);
  const active = useMemo(() => accountsByVersion(pubs), [pubs]);
  const langs = useMemo(() => languagesOf(cands), [cands]);
  const init = useMemo(() => {
    const iv = cands.find((c) => c.version_id === initialVersion);
    const lang = iv?.language_code ?? langs[0] ?? "en";
    const acct = defaultAccount(accounts, lang, d.series.category, cats);
    return { lang, accountId: acct?.id ?? null, selected: iv ? [iv.version_id] : defaultSelection(cands, lang, active, acct?.id) };
    // Only the first render's data decides the defaults; later SSE refreshes must not reset the owner's choices.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [lang, setLang] = useState(init.lang);
  const [selected, setSelected] = useState<string[]>(init.selected);
  const [accountId, setAccountId] = useState<string | null>(init.accountId);
  const [skipped, setSkipped] = useState(0);
  const [mode, setMode] = useState<Mode>("queue");
  const [interleave, setInterleave] = useState(false); // M2.1: opt-in — mixing moves other series' planned posts
  const [notify, setNotify] = useState(true);
  const [times, setTimes] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<PublishPlanResult | null>(null);

  const account = accounts.find((a) => a.id === accountId);
  const ordered = cands.filter((c) => selected.includes(c.version_id)).map((c) => c.version_id);
  const request = (dry_run: boolean): z.input<typeof PublishPlanRequest> => ({
    version_ids: ordered, account_id: accountId!, mode,
    ...(mode === "at" ? { at: ordered.map((id) => fromLocalInput(times[id] ?? "") ?? "") } : {}),
    ...(mode === "queue" ? { interleave } : {}),
    notify_subscribers: notify,
    // The plan-wide flag loses to a version's own YouTube metadata (imported videos carry notify_subscribers), so the owner's
    // choice is also sent per version — overrides are layered last by the API.
    metadata_overrides: Object.fromEntries(ordered.map((id) => [id, { notify_subscribers: notify }])),
    dry_run,
  });
  const dry = useMutation(() => api.createPublishPlan(request(true)), { onSuccess: (r) => { setPreview(r); setStep("preview"); } });
  const confirm = useMutation(() => api.createPublishPlan(request(false)), {
    invalidate: ["publications", "dashboard", "queues", "series"],
    onSuccess: (r) => { const n = r.publications.filter((p) => ordered.includes(p.version_id)).length; toast.ok(`Scheduled ${n} video${n === 1 ? "" : "s"}.`); navigate("/publish"); },
    onError: (e) => toast.error(e, "Couldn't schedule"),
  });

  const pickLang = (l: string) => {
    setLang(l);
    const acct = defaultAccount(accounts, l, d.series.category, cats);
    setAccountId(acct?.id ?? null);
    setSelected(defaultSelection(cands, l, active, acct?.id));
    setSkipped(0);
  };
  const pickAccount = (id: string) => {
    setAccountId(id);
    const keep = selected.filter((v) => !active.get(v)?.has(id));
    setSkipped(selected.length - keep.length);
    setSelected(keep);
  };
  const goMode = () => { if (mode === "at") setTimes((t) => ({ ...defaultTimes(ordered), ...t })); setStep("mode"); };
  const timesValid = mode !== "at" || ordered.every((id) => { const iso = fromLocalInput(times[id] ?? ""); return iso !== null && Date.parse(iso) > Date.now(); });

  const back = { versions: "series", account: "versions", mode: "account", preview: "mode", series: "series" } as const;
  const bar = (next: ReactNode) => (
    <div className="wizard-bar">
      <Button variant="ghost" size="lg" onClick={() => { setPreview(null); setStep(back[step]); }}>Back</Button>
      {next}
    </div>
  );

  if (step === "versions") {
    const shown = cands.filter((c) => c.language_code === lang);
    return (
      <>
        {langs.length > 1 && <Segmented label="Language" value={lang} onChange={pickLang} options={langs.map((l) => ({ value: l, label: languageName(l) }))} />}
        {shown.length ? (
          <Card title={`Approved videos · ${languageName(lang)}`} action={
            <span className="row">
              <button type="button" className="text-link" onClick={() => setSelected(shown.map((c) => c.version_id))}>All</button>
              <button type="button" className="text-link" onClick={() => setSelected([])}>None</button>
            </span>}>
            <ul className="pick-list">
              {shown.map((c) => <VersionPick key={c.version_id} c={c} checked={selected.includes(c.version_id)} on={accountNames(active.get(c.version_id), accounts)} onToggle={() => setSelected((s) => s.includes(c.version_id) ? s.filter((x) => x !== c.version_id) : [...s, c.version_id])} />)}
            </ul>
          </Card>
        ) : <EmptyState icon="check" title="No approved videos in this language" />}
        {bar(<Button variant="primary" size="lg" disabled={!ordered.length} onClick={() => setStep("account")}>Next · {ordered.length} selected</Button>)}
      </>
    );
  }

  if (step === "account") {
    const yts = accounts.filter((a) => a.platform === "youtube");
    return (
      <>
        {yts.length ? (
          <fieldset className="contact-pick">
            <legend className="field-label">Post to</legend>
            {yts.map((a) => (
              <label key={a.id} className={`radio-row ${isUsable(a) ? "" : "is-disabled"}`}>
                <input type="radio" name="account" checked={accountId === a.id} disabled={!isUsable(a)} onChange={() => pickAccount(a.id)} />
                <span className="list-main">
                  <span className="list-title">{a.name}</span>
                  <span className="list-sub">{[a.profile.title, a.language_code ? languageName(a.language_code) : null, categoryName(a.category, cats), !isUsable(a) ? (a.auth_status === "disconnected" ? "Connect in Settings first" : "Reconnect in Settings first") : null].filter(Boolean).join(" · ")}</span>
                </span>
                {a.language_code === lang && sameCategory(a.category, d.series.category, cats) && isUsable(a) && <Badge tone="ok">Best match</Badge>}
              </label>
            ))}
          </fieldset>
        ) : <EmptyState icon="publish" title="No YouTube account yet" action={<Link to="/settings/accounts" className="btn btn-secondary btn-md"><span>Add an account</span></Link>} />}
        {account && account.language_code !== lang && <p className="field-error">This account posts in {languageName(account.language_code ?? "")}, but these videos are {languageName(lang)}.</p>}
        {skipped > 0 && <p className="note" role="status">{skipped} video{skipped === 1 ? " is" : "s are"} already scheduled on this account, so {skipped === 1 ? "it was" : "they were"} left out.</p>}
        {bar(<Button variant="primary" size="lg" disabled={!account || !ordered.length} onClick={goMode}>Next</Button>)}
      </>
    );
  }

  if (step === "mode") {
    const queue = queueFor(queues.data ?? [], account, cats);
    return (
      <>
        <Segmented label="When" value={mode} onChange={(m) => { setMode(m); if (m === "at") setTimes((t) => ({ ...defaultTimes(ordered), ...t })); }}
          options={[{ value: "queue", label: "Next free slots" }, { value: "at", label: "Pick times" }, { value: "now", label: "Now" }]} />
        {mode === "queue" && (
          <Card>
            {queue ? (
              <>
                <p><b>{queue.name}</b> <span className="muted small">· {queue.slots.join(", ")} · {queue.timezone}{queue.paused_at || !queue.enabled ? " · paused" : ""}</span></p>
                {queue.next_free_slots.length > 0 && <p className="muted small">Next free: {queue.next_free_slots.slice(0, Math.max(3, Math.min(ordered.length, 5))).map((t) => dateTime(t)).join(" · ")}</p>}
              </>
            ) : <p className="field-error">No queue for this account's category and language yet. <Link to="/settings/queues" className="text-link">Create one</Link></p>}
            <Switch label="Mix with other series" checked={interleave} onChange={setInterleave}
              hint="Off: these go into the free slots and nothing else moves. On: takes turns with other series' planned posts on this channel (the preview lists every post it moves). Each series keeps its own order." />
          </Card>
        )}
        <Card>
          <Switch label="Notify subscribers" checked={notify} onChange={setNotify}
            hint={looksLikeBackfill(cands.filter((c) => ordered.includes(c.version_id))) ? "Looks like a backfill of older videos — consider turning this off so subscribers aren't flooded." : "YouTube tells subscribers about each new video."} />
        </Card>
        {mode === "at" && (
          <Card title="Times (your time zone)">
            {ordered.map((id) => {
              const c = cands.find((x) => x.version_id === id)!;
              return (
                <div key={id} className="field">
                  <label className="field-label" htmlFor={`at-${id}`}>Ep {c.sequence} · {c.title}</label>
                  <input id={`at-${id}`} type="datetime-local" value={times[id] ?? ""} onChange={(e) => setTimes((t) => ({ ...t, [id]: e.target.value }))} />
                </div>
              );
            })}
            {!timesValid && <p className="field-error">Every time must be in the future.</p>}
          </Card>
        )}
        {mode === "now" && <Card><p>Uploads start right away, one after another, in series order.</p></Card>}
        {dry.error ? <ErrorNote error={dry.error} /> : null}
        {bar(<Button variant="primary" size="lg" busy={dry.pending} disabled={!timesValid} onClick={() => void dry.mutate()}>Preview</Button>)}
      </>
    );
  }

  // preview
  const res = preview;
  if (!res) return bar(null);
  const auditPending = yt.data?.api_audited === false || res.warnings.some(isAuditWarning) || res.publications.some((p) => p.hold_reason === "awaiting_api_audit");
  const other = res.warnings.filter((w) => !isAuditWarning(w));
  const moved = res.moved ?? [];
  // With interleave the API also returns this account's re-flowed posts; the list shows what THIS plan schedules (moves are listed separately).
  const mine = res.publications.filter((p) => ordered.includes(p.version_id));
  return (
    <>
      {auditPending && (
        <div className="audit-alert" role="alert">
          <Icon name="lock" />
          <div><p><b>{AUDIT_WARNING}</b></p><p className="small"><Link to="/settings/accounts#youtube" className="text-link">About the audit</Link></p></div>
        </div>
      )}
      {other.length > 0 && <ul className="warnings">{other.map((w) => <li key={w}><Icon name="alert" size={18} /> {w}</li>)}</ul>}
      {moved.length > 0 && <MovedPosts moved={moved} accountId={accountId!} />}
      <Card title={`${mine.length} post${mine.length === 1 ? "" : "s"} to ${account ? `${platformName(account.platform)} · ${account.name}` : ""}`}>
        <ol className="preview-list">
          {mine.map((p) => (
            <li key={p.id}>
              <span className="list-main"><span className="list-title">Ep {p.episode.sequence} · {p.episode.title}</span><span className="list-sub">{p.scheduled_at ? dateTime(p.scheduled_at) : "As soon as possible"}</span></span>
              <Badge tone={stateChip(p).tone}>{stateChip(p).label}</Badge>
            </li>
          ))}
        </ol>
      </Card>
      {(res.playlists ?? []).length > 0 && (
        <ul className="preview-notes" aria-label="Playlists">
          {res.playlists!.map((pl) => (
            <li key={pl.series_id}><Icon name="book" size={18} /> Adds to playlist «{pl.title}»{pl.exists ? "" : " (created with the first upload)"}{pl.privacy !== "public" ? ` · ${pl.privacy}` : ""}</li>
          ))}
        </ul>
      )}
      {bar(<Button variant="primary" size="lg" busy={confirm.pending} disabled={!mine.length} onClick={() => void confirm.mutate()}>Schedule {mine.length} video{mine.length === 1 ? "" : "s"}</Button>)}
    </>
  );
}

/** "This moves 2 other posts: …" — interleave re-flows other series' planned posts; the owner sees every move before confirming. */
function MovedPosts({ moved, accountId }: { moved: NonNullable<PublishPlanResult["moved"]>; accountId: string }) {
  const pubs = usePublications({ account_id: accountId }).data ?? [];
  return (
    <Card title={`This moves ${moved.length} other post${moved.length === 1 ? "" : "s"}`} tone="attention">
      <ul className="preview-list" aria-label="Posts that move">
        {moved.map((m) => {
          const p = pubs.find((x) => x.id === m.publication_id);
          return (
            <li key={m.publication_id}>
              <span className="list-main">
                <span className="list-title">{p ? `${p.series.title} · Ep ${p.episode.sequence} · ${p.episode.title}` : "Another post"}</span>
                <span className="list-sub">{m.from ? dateTime(m.from) : "unscheduled"} → <b>{dateTime(m.to)}</b></span>
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function accountNames(ids: Set<string> | undefined, accounts: Account[]): string[] {
  return ids ? [...ids].map((id) => accounts.find((a) => a.id === id)?.name ?? "another account") : [];
}

function VersionPick({ c, checked, on, onToggle }: { c: Candidate; checked: boolean; on: string[]; onToggle: () => void }) {
  const id = `v-${c.version_id}`;
  return (
    <li className={`pick-row ${checked ? "is-selected" : ""}`}>
      <input id={id} type="checkbox" checked={checked} onChange={onToggle} />
      <label htmlFor={id} className="list-main">
        <span className="list-title"><span className="muted">{c.sequence}.</span> {c.title}</span>
        <span className="list-sub">{[`v${c.version}`, c.imported ? "imported" : null, on.length ? `already on ${on.join(", ")}` : null].filter(Boolean).join(" · ")}</span>
      </label>
    </li>
  );
}
